import {
  Upload,
  UserCircle2,
  FileText,
  Mic,
  Youtube,
  Newspaper,
  GraduationCap,
  Briefcase,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUsage } from "@/lib/api";

interface EmptyStateProps {
  onUpload: () => void;
}

interface Step {
  number: number;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

const STEPS: Step[] = [
  {
    number: 1,
    title: "Upload",
    description: "Drop a video or audio file — interview, lecture, podcast, meeting.",
    icon: Upload,
  },
  {
    number: 2,
    title: "What type of content is this?",
    description: "Pick the content type so reports are framed for the right audience.",
    icon: UserCircle2,
  },
  {
    number: 3,
    title: "Get tailored reports",
    description: "Summary, key points, action items, and full transcript — in minutes.",
    icon: FileText,
  },
];

const PERSONA_PREVIEW = [
  { label: "Podcaster", icon: Mic },
  { label: "YouTuber", icon: Youtube },
  { label: "Journalist", icon: Newspaper },
  { label: "Lecturer", icon: GraduationCap },
  { label: "Business", icon: Briefcase },
  { label: "Just transcript", icon: FileText },
];

export function EmptyState({ onUpload }: EmptyStateProps) {
  const { data: usage } = useUsage();
  const freeMinutes = usage?.limit ?? usage?.limit_minutes ?? 300;
  return (
    <div className="flex items-center justify-center h-full overflow-y-auto p-6">
      <div className="w-full max-w-3xl space-y-10 py-8">
        <div className="text-center space-y-3">
          <h1 className="text-3xl font-semibold tracking-tight">Welcome to Eduarta</h1>
          <p className="text-base text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Upload a meeting, lecture, interview, podcast, or webinar — get AI reports tailored to your needs.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {STEPS.map((step) => {
            const Icon = step.icon;
            return (
              <div
                key={step.number}
                className="rounded-xl border border-border bg-card p-5 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
                    <Icon className="w-5 h-5 text-foreground" />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground tabular-nums">
                    Step {step.number}
                  </span>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-foreground">{step.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col items-center gap-3">
          <Button size="lg" className="gap-2" onClick={onUpload}>
            <Upload className="w-4 h-4" />
            Upload your first file
          </Button>
          <p className="text-xs text-muted-foreground">
            Free plan includes {freeMinutes} minutes of content.
          </p>
        </div>

        <div className="space-y-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground text-center">
            Available roles
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {PERSONA_PREVIEW.map((persona) => {
              const Icon = persona.icon;
              return (
                <span
                  key={persona.label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-3 py-1 text-xs text-muted-foreground"
                >
                  <Icon className="w-3.5 h-3.5" />
                  {persona.label}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
